import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import DocumentFilters from './components/DocumentFilters';
import DocumentUploadZone from './components/DocumentUploadZone';
import DocumentTableRow from './components/DocumentTableRow';
import DocumentCard from './components/DocumentCard';
import DocumentEditModal from './components/DocumentEditModal';
import BulkActionsBar from './components/BulkActionsBar';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import BulkCategoryModal from './components/BulkCategoryModal';
import EmptyState from './components/EmptyState';
import DocumentUploadModal from './components/DocumentUploadModal';
import { documentService } from '../../services/documentService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import useSiteReadOnly, { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import { supabase } from '../../lib/supabase';
import { useLanguage } from '../../contexts/LanguageContext';


import Skeleton from '../../components/ui/Skeleton';

const DocumentManagement = () => {
  const [searchParams] = useSearchParams();
  const filterSiteId = searchParams?.get('siteId');
  const filterDocumentId = searchParams?.get('documentId');
  const filterSearch = searchParams?.get('search');
  const filterCategory = searchParams?.get('category');
  const filterStatus = searchParams?.get('status');

  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const role = userProfile?.role || 'admin';
  const userId = userProfile?.id;

  const [documents, setDocuments] = useState([]);
  const [sites, setSites] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState(filterSearch || '');
  const [selectedSite, setSelectedSite] = useState(filterSiteId || 'all');
  // Page-level banner fires only when filter is on a specific (non-'all') site.
  const gatedSiteId = selectedSite && selectedSite !== 'all' ? selectedSite : null;
  const { status: gatedSiteStatus, isReadOnly: gatedSiteReadOnly } = useSiteReadOnly(gatedSiteId);
  const [selectedCategory, setSelectedCategory] = useState(filterCategory || 'all');
  const [selectedStatus, setSelectedStatus] = useState(filterStatus || 'all');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);
  // Inline banner for backend-delete failures. Kept separate from the
  // page-load `error` state (which renders a full-screen retry view) so a
  // failed delete doesn't blank the list the user just acted on.
  const [deleteError, setDeleteError] = useState(null);
  const [isBulkCategoryModalOpen, setIsBulkCategoryModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewMode, setViewMode] = useState('table');
  const [owners, setOwners] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadStatusLabel, setUploadStatusLabel] = useState('');
  // NSherjFh PR 2: tab between active documents (default) and the
  // soft-deleted recycle-bin view. Switching re-fetches with the matching
  // `includeDeleted` flag.
  const [viewDeleted, setViewDeleted] = useState(false);

  useEffect(() => {
    loadData();
  }, [viewDeleted]);

  useEffect(() => {
    // Apply URL filters when data is loaded
    if (filterSiteId && sites?.length > 0) {
      setSelectedSite(filterSiteId);
    }
  }, [filterSiteId, sites]);

  const loadData = async () => {
    try {
      setLoading(true);

      const companyResult = await companyService?.getUserCompany();

      if (companyResult?.error) {
        // silently handle company lookup warning
      } else {
        setCompanyId(companyResult?.data?.id);
      }

      // Scope sites to company (RLS ensures site_manager only sees their company's sites)
      const companyId = companyResult?.data?.id;
      const [sitesResult, documentsResult] = await Promise.all([
        companyId
          ? siteService?.getSitesByCompany(companyId)
          : siteService?.getAllSites(),
        documentService?.getAllDocuments({ includeDeleted: viewDeleted })
      ]);

      if (sitesResult?.error) throw sitesResult.error;
      if (documentsResult?.error) throw documentsResult.error;

      setSites(sitesResult?.data);
      // In the deleted-documents tab, the service returns BOTH active and
      // deleted rows (includeDeleted: true is "include", not "only"); we
      // narrow to the soft-deleted ones here. Active tab already comes
      // pre-filtered by the service default.
      const allDocs = documentsResult?.data || [];
      setDocuments(viewDeleted ? allDocs?.filter(d => d?.deleted_at) : allDocs);
    } catch (err) {
      console.error('[DocumentManagement] load documents failed:', err);
      setError('Failed to load documents. Please refresh the page.');
      toast?.error('Failed to load documents. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const getDocumentStatus = (expiryDate) => {
    if (!expiryDate) return 'no-expiry';
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    if (isNaN(expiry?.getTime())) return 'no-expiry';
    const diffMs = expiry - today;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring-soon';
    return 'valid';
  };

  const filteredDocuments = useMemo(() => {
    let result = [...documents];

    if (searchQuery) {
      result = result?.filter(doc =>
        doc?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        doc?.owner?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        doc?.sites?.site_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (selectedSite !== 'all') {
      result = result?.filter(doc => doc?.site_id === selectedSite);
    }

    if (selectedCategory !== 'all') {
      result = result?.filter(doc => doc?.category === selectedCategory);
    }

    if (selectedStatus !== 'all') {
      result = result?.filter(doc => getDocumentStatus(doc?.expiry_date || doc?.expiryDate) === selectedStatus);
    }

    return result;
  }, [documents, searchQuery, selectedSite, selectedCategory, selectedStatus]);

  const hasActiveFilters = searchQuery || selectedSite !== 'all' || 
                          selectedCategory !== 'all' || selectedStatus !== 'all';

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedSite('all');
    setSelectedCategory('all');
    setSelectedStatus('all');
  };

  const handleSelectDocument = (id, checked) => {
    if (checked) {
      setSelectedDocuments(prev => [...prev, id]);
    } else {
      setSelectedDocuments(prev => prev?.filter(docId => docId !== id));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedDocuments(filteredDocuments?.map(doc => doc?.id));
    } else {
      setSelectedDocuments([]);
    }
  };

  const handleEdit = (document) => {
    setEditingDocument(document);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = (updatedDocument) => {
    setDocuments(prev => prev?.map(doc => 
      doc?.id === updatedDocument?.id ? updatedDocument : doc
    ));
    setIsEditModalOpen(false);
    setEditingDocument(null);
  };

  const handleDelete = (document) => {
    setDeletingDocument(document);
    setIsDeleteModalOpen(true);
  };

  // Persisting delete to the backend. Previous version filtered local React
  // state without calling documentService.deleteDocument, so deletes appeared
  // to succeed but rows reappeared on refresh. See zKrjhNRJ.
  //
  // Partial success on bulk: ids that succeed are removed from view; ids
  // that fail stay in the list and surface in the inline error banner.
  const handleConfirmDelete = async () => {
    if (!deletingDocument) {
      setIsDeleteModalOpen(false);
      return;
    }
    const ids = Array.isArray(deletingDocument)
      ? deletingDocument
      : [deletingDocument?.id];
    try {
      const results = await Promise.all(
        ids?.map(id => documentService?.deleteDocument(id))
      );
      const succeededIds = ids?.filter((_, i) => !results?.[i]?.error);
      const failedIds = ids?.filter((_, i) => results?.[i]?.error);

      if (succeededIds?.length > 0) {
        setDocuments(prev => prev?.filter(doc => !succeededIds?.includes(doc?.id)));
        setDeleteError(null);
      }
      if (Array.isArray(deletingDocument)) {
        setSelectedDocuments(prev => prev?.filter(id => !succeededIds?.includes(id)));
      }
      if (failedIds?.length > 0) {
        const firstErrMsg = results?.find(r => r?.error)?.error?.message || 'Unknown error';
        const titlesById = (id) => documents?.find(d => d?.id === id)?.title || id;
        const failedTitles = failedIds?.map(titlesById)?.join(', ');
        console.error('[DocumentManagement] delete failed:', { failedIds, results });
        setDeleteError({
          message: failedIds?.length === 1
            ? `Failed to delete "${failedTitles}": ${firstErrMsg}`
            : `Failed to delete ${failedIds?.length} documents (${failedTitles}). First error: ${firstErrMsg}`,
        });
      }
    } catch (err) {
      console.error('[DocumentManagement] delete threw:', err);
      setDeleteError({ message: err?.message || 'Failed to delete document(s). Please try again.' });
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingDocument(null);
    }
  };

  // NSherjFh PR 2: restore a soft-deleted document. Optimistically drops
  // it from the deleted-tab list; switching to the active tab will pick it
  // back up via the next loadData.
  const handleRestore = async (document) => {
    try {
      const result = await documentService?.restoreDocument(document?.id);
      if (result?.error) throw result?.error;
      setDocuments(prev => prev?.filter(d => d?.id !== document?.id));
      toast?.success(`"${document?.title}" restored`);
    } catch (err) {
      console.error('[DocumentManagement] restore failed:', err);
      toast?.error(`Failed to restore: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleBulkDelete = () => {
    setDeletingDocument(selectedDocuments);
    setIsDeleteModalOpen(true);
  };

  const handleBulkCategoryChange = () => {
    setIsBulkCategoryModalOpen(true);
  };

  const handleConfirmBulkCategory = (newCategory) => {
    const categoryLabels = {
      rams: 'H&S Docs',
      insurance: 'Insurance',
      training: 'Training Certificates',
      plant: 'Plant Certificates'
    };

    setDocuments(prev => prev?.map(doc => 
      selectedDocuments?.includes(doc?.id) 
        ? { ...doc, category: newCategory, categoryLabel: categoryLabels?.[newCategory] }
        : doc
    ));
    setSelectedDocuments([]);
    setIsBulkCategoryModalOpen(false);
  };

  const handleDownload = (document) => {
    // download handled by browser
  };

  const handleFileSelect = async (files) => {
    if (subscriptionReadOnly) {
      toast?.error('Your payment is overdue. Update payment to upload documents.');
      return;
    }
    if (gatedSiteReadOnly) {
      toast?.error('This site is completed — please reactivate to continue.');
      return;
    }
    // Open the upload modal with the first file pre-selected
    setPendingFiles(files);
    setIsUploadModalOpen(true);
  };

  const handleUploadSubmit = async ({ file, title, category, owner, issueDate, expiryDate, notes, siteId }) => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatusLabel('Uploading...');
    try {
      const targetCompanyId = companyId;
      if (!targetCompanyId) throw new Error('Company not found. Please refresh and try again.');
      if (!siteId) throw new Error('Please select a site.');

      // Site-read-only submission-time check. The site picked in the modal
      // may differ from the page-level filter, so we re-check on submit.
      const { data: pickedSite } = await supabase
        ?.from('sites')
        ?.select('status')
        ?.eq('id', siteId)
        ?.maybeSingle();
      if (isSiteReadOnlyStatus(pickedSite?.status)) {
        toast?.error('This site is completed — please reactivate to continue.');
        setIsUploading(false);
        return;
      }

      let result = await documentService?.uploadDocument({
        file,
        title,
        category,
        owner,
        issueDate,
        expiryDate,
        notes,
        siteId,
        companyId: targetCompanyId,
        onProgress: (percent) => {
          setUploadProgress(percent);
          if (percent >= 95 && percent < 100) {
            setUploadStatusLabel('Saving record...');
          } else if (percent === 100) {
            setUploadStatusLabel('Complete!');
          } else {
            setUploadStatusLabel(`Uploading... ${percent}%`);
          }
        }
      });

      if (result?.error) {
        const err = result?.error;
        const code = err?.code || 'unknown';
        let humanMsg = 'Document upload failed.';
        if (code === '42501' || err?.message?.includes('RLS') || err?.message?.includes('permission')) {
          humanMsg = 'Permission denied. You may not have access to upload to this site.';
        } else if (err?.message?.includes('storage') || err?.message?.includes('bucket')) {
          humanMsg = 'Storage upload failed. Please check your connection and try again.';
        }
        toast?.error(humanMsg, { duration: 7000 });
        return;
      }

      toast?.success(`"${title}" uploaded successfully!`);
      setIsUploadModalOpen(false);
      setPendingFiles([]);
      await loadData();
    } catch (err) {
      console.error('[DocumentManagement] upload failed:', err);
      const code = err?.code || 'unknown';
      toast?.error('Upload failed. Please try again.', { duration: 7000 });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatusLabel('');
      setPendingFiles([]);
    }
  };

  const allSelected = filteredDocuments?.length > 0 && 
                     selectedDocuments?.length === filteredDocuments?.length;
  const someSelected = selectedDocuments?.length > 0 && 
                      selectedDocuments?.length < filteredDocuments?.length;

  // Build filter state object to pass to row/card for back navigation
  const filterState = {
    searchQuery,
    selectedSite,
    selectedCategory,
    selectedStatus
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="h-8 bg-muted rounded w-64 mb-2 animate-pulse" />
            <div className="h-4 bg-muted rounded w-80 mb-8 animate-pulse" />
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    {['', 'Document', 'Category', 'Owner', 'Expiry', 'Status', 'Actions']?.map((h) => (
                      <th key={h} className="px-4 py-4 text-left text-sm font-semibold text-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <Skeleton variant="table-row" count={6} cols={7} />
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <p className="text-destructive">{error}</p>
            <Button onClick={loadData} className="mt-4">Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <Breadcrumb items={[
            { label: t('home'), href: '/' },
            { label: t('documentManagement'), href: '/documents' }
          ]} />

          <SiteReadOnlyBanner status={gatedSiteStatus} siteId={gatedSiteId} className="mb-6" />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                {viewDeleted ? 'Deleted documents' : t('documentManagement')}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {viewDeleted
                  ? 'Documents removed from active views. Restore to bring them back.'
                  : t('documentManagementSubtitle')}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* NSherjFh PR 2: tab between active and soft-deleted documents.
                  Admin-only — non-admin roles never see the toggle. */}
              {role === 'admin' && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  onClick={() => {
                    setSelectedDocuments([]);
                    setDeleteError(null);
                    setViewDeleted(v => !v);
                  }}
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  iconPosition="left"
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
              <Button
                variant={viewMode === 'table' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('table')}
                aria-label={t('tableView')}
              >
                <Icon name="Table" size={20} />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
                aria-label={t('gridView')}
              >
                <Icon name="LayoutGrid" size={20} />
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Upload is meaningless in the deleted-documents tab. */}
            {!viewDeleted && (
              <DocumentUploadZone
                onFileSelect={handleFileSelect}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
              />
            )}

            <DocumentFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedSite={selectedSite}
              onSiteChange={setSelectedSite}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              onClearFilters={handleClearFilters}
              sites={sites}
              hasActiveFilters={hasActiveFilters}
            />

            {deleteError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <div className="flex-1">
                  <p className="text-sm text-destructive font-medium">{deleteError?.message}</p>
                </div>
                <button
                  onClick={() => setDeleteError(null)}
                  className="text-destructive hover:text-destructive/80"
                  aria-label="Dismiss"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}

            {filteredDocuments?.length === 0 ? (
              <div className="bg-card rounded-lg border border-border shadow-elevation-2">
                <EmptyState
                  hasFilters={hasActiveFilters}
                  onClearFilters={handleClearFilters}
                  onUpload={() => document.querySelector('input[type="file"]')?.click()}
                />
              </div>
            ) : (
              <>
                {viewMode === 'table' ? (
                  <div className="bg-card rounded-lg border border-border shadow-elevation-2 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted border-b border-border">
                          <tr>
                            <th className="px-4 py-4 text-left">
                              <Checkbox
                                checked={allSelected}
                                onChange={(e) => handleSelectAll(e?.target?.checked)}
                                indeterminate={someSelected}
                              />
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold text-foreground">
                              {t('documentHeader')}
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold text-foreground">
                              {t('category')}
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold text-foreground">
                              {t('owner')}
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold text-foreground">
                              {t('issueDate')}
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold text-foreground">
                              {t('expiryDate')}
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold text-foreground">
                              {t('status')}
                            </th>
                            <th className="px-4 py-4 text-left text-sm font-semibold text-foreground">
                              {t('actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDocuments?.map(document => (
                            <DocumentTableRow
                              key={document?.id}
                              document={document}
                              isSelected={selectedDocuments?.includes(document?.id)}
                              onSelect={handleSelectDocument}
                              onEdit={!viewDeleted && role !== 'client' && role !== 'worker' ? handleEdit : null}
                              onDelete={!viewDeleted && role === 'admin' ? handleDelete : null}
                              onRestore={viewDeleted && role === 'admin' ? handleRestore : null}
                              onDownload={handleDownload}
                              filterState={filterState}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {filteredDocuments?.map(document => (
                      <DocumentCard
                        key={document?.id}
                        document={document}
                        isSelected={selectedDocuments?.includes(document?.id)}
                        onSelect={handleSelectDocument}
                        onEdit={!viewDeleted && role !== 'client' && role !== 'worker' ? handleEdit : null}
                        onDelete={!viewDeleted && role === 'admin' ? handleDelete : null}
                        onRestore={viewDeleted && role === 'admin' ? handleRestore : null}
                        onDownload={handleDownload}
                        filterState={filterState}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      {/* Bulk actions only make sense on the active-documents tab. The
          deleted-documents tab uses per-row Restore for now. */}
      {!viewDeleted && (
        <BulkActionsBar
          selectedCount={selectedDocuments?.length}
          onClearSelection={() => setSelectedDocuments([])}
          onBulkCategoryChange={handleBulkCategoryChange}
          onBulkDelete={handleBulkDelete}
        />
      )}
      <DocumentEditModal
        document={editingDocument}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingDocument(null);
        }}
        onSave={handleSaveEdit}
        sites={sites}
        owners={owners}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingDocument(null);
        }}
        onConfirm={handleConfirmDelete}
        documentCount={Array.isArray(deletingDocument) ? deletingDocument?.length : 1}
        documentTitle={!Array.isArray(deletingDocument) ? deletingDocument?.title : ''}
      />
      <BulkCategoryModal
        isOpen={isBulkCategoryModalOpen}
        onClose={() => setIsBulkCategoryModalOpen(false)}
        onConfirm={handleConfirmBulkCategory}
        documentCount={selectedDocuments?.length}
      />
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => { setIsUploadModalOpen(false); setPendingFiles([]); }}
        onUpload={handleUploadSubmit}
        sites={sites}
        defaultSiteId={selectedSite !== 'all' ? selectedSite : (sites?.[0]?.id || '')}
      />
    </div>
  );
};

export default DocumentManagement;